
// export default PlacesAutocompleteComponent;
import React, { useRef, useState } from "react";
import { Col } from "react-bootstrap";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

const PlacesAutocompleteComponent = ({
  name,
  value,
  setFieldValue,
  setFieldTouched,
  searchOptions,
  className,
  handleFieldBlur,
  setFieldError,
  onAdresseSelected,
  hasCoordinates,
}) => {
  const [selectedFromSuggestions, setSelectedFromSuggestions] = useState(false);
  // Un geocodage est en cours : on ne veut pas en lancer un second par-dessus.
  const [resolving, setResolving] = useState(false);
  // Horodatage de la derniere selection depuis les suggestions, et dernier texte
  // deja tente sans succes. Les deux servent a ne pas geocoder pour rien.
  const derniereSelection = useRef(0);
  const dernierTexteTente = useRef(null);
  // `hasCoordinates` capture a l'entree du blur serait PERIME : cliquer une puce
  // d'adresse pose les coordonnees PENDANT la pause de 250 ms, et la puce
  // n'horodate pas `derniereSelection` comme le fait une suggestion Google. On
  // relit donc l'etat courant apres l'attente, via cette ref tenue a jour a
  // chaque rendu. Sans elle, le rattrapage repartait sur l'ancien texte et
  // affichait « Adresse non reconnue » sur l'adresse que la puce venait de poser.
  const coordonneesRef = useRef(hasCoordinates);
  coordonneesRef.current = hasCoordinates;

  /**
   * Les coordonnees du champ courant, ecrites au seul endroit qui les connaisse.
   *
   * La correspondance champ -> colonnes etait recopiee dans chaque chemin ; le
   * rattrapage au blur en aurait fait une troisieme copie, et c'est la que
   * l'erreur se glisse — une adresse d'enlevement qui ecrirait les coordonnees
   * de livraison enverrait le camion au mauvais endroit.
   */
  const poserCoordonnees = (lat, lng) => {
    const champs =
      name === "pickupAdr"
        ? ["altitude_Fournisseur", "longitude_Fournisseur"]
        : name === "address"
        ? ["altitude_Livreur", "longitude_Livreur"]
        : null;

    if (!champs) return;

    const [champLat, champLng] = champs;
    // Jamais de coordonnees nulles : les colonnes sont NOT NULL cote base, et une
    // valeur vide y provoquait un 500 a l'enregistrement.
    if (lat === null || lat === undefined || lng === null || lng === undefined) return;

    setFieldValue(champLat, lat);
    setFieldValue(champLng, lng);
    setFieldTouched(champLat, true);
    setFieldTouched(champLng, true);
  };
 
  const handleAddressChange = (addr) => {
    setFieldValue(name, addr); // Met à jour la valeur dans Formik
    setFieldTouched(name, true); // Marque le champ comme touché immédiatement
    if (!selectedFromSuggestions) {
      setFieldError(
        name,
        "Vous devez sélectionner une adresse depuis les suggestions !"
      );
    }
       if (name === "pickupAdr") {
      setFieldValue("altitude_Fournisseur", "");
      setFieldValue("longitude_Fournisseur", "");
      setFieldTouched("altitude_Fournisseur", false);
      setFieldTouched("longitude_Fournisseur", false);
    }
    else if (name === "address") {
      setFieldValue("altitude_Livreur", "");
      setFieldValue("longitude_Livreur", "");
      setFieldTouched("altitude_Livreur", false);
      setFieldTouched("longitude_Livreur", false);
    }
 
  };

  const DELAI_AVANT_RATTRAPAGE = 250;

  /**
   * Rattraper une adresse tapee sans passer par les suggestions.
   *
   * L'ancien comportement se contentait de refuser : l'ops voyait son adresse a
   * l'ecran, un message lui demandait de la choisir dans une liste, et le
   * formulaire restait bloque sur une adresse pourtant valide. On la resout.
   */
  const customHandleBlur = async () => {
    setFieldTouched(name, true);
    if (handleFieldBlur) handleFieldBlur();

    if (selectedFromSuggestions) return;

    const texte = (value || "").trim();

    // Rien a rattraper : champ vide, coordonnees DEJA POSEES, appel en cours, ou
    // texte deja tente.
    //
    // `hasCoordinates` est la garde qui manquait : une adresse choisie dans les
    // puces du client arrive avec ses coordonnees, mais sans passer par la
    // selection Google — le composant la croyait donc tapee a la main et tentait
    // de la geocoder, pour finir par afficher « Adresse non reconnue » sur une
    // adresse parfaitement valide.
    if (!texte || hasCoordinates || resolving || dernierTexteTente.current === texte) {
      return;
    }

    // Cliquer une suggestion declenche d'abord le blur : sans cette pause, on
    // geocoderait le texte partiel et la course avec la selection deciderait
    // quelle adresse l'emporte, donc ou va le camion. On laisse le clic passer
    // devant.
    const avant = Date.now();
    await new Promise((resolve) => setTimeout(resolve, DELAI_AVANT_RATTRAPAGE));

    // Deux raisons de renoncer apres la pause : une suggestion a ete choisie, ou
    // des coordonnees ont ete posees autrement — une puce d'adresse du client.
    if (derniereSelection.current > avant || coordonneesRef.current) return;

    dernierTexteTente.current = texte;
    setResolving(true);

    try {
      const results = await geocodeByAddress(texte);

      // Plusieurs correspondances : en choisir une reviendrait a deviner une
      // destination. On le dit plutot que de trancher a la place de l'ops.
      if (results.length !== 1) {
        setFieldError(
          name,
          results.length === 0
            ? "Adresse introuvable — reprenez-la dans la liste de suggestions."
            : "Adresse imprécise — reprenez-la dans la liste de suggestions."
        );
        return;
      }

      const { lat, lng } = await getLatLng(results[0]);
      const adresse = results[0].formatted_address || texte;

      setFieldValue(name, adresse);
      setSelectedFromSuggestions(true);
      setFieldError(name, "");
      poserCoordonnees(lat, lng);

      if (onAdresseSelected) onAdresseSelected();
    } catch (error) {
      console.error("Rattrapage d'adresse au blur:", error);
      setFieldError(
        name,
        "Adresse non reconnue — reprenez-la dans la liste de suggestions."
      );
    } finally {
      setResolving(false);
    }
  };

  const handleAddressSelect = async (address,event) => {
    try {
      const results = await geocodeByAddress(address);
      if (results.length > 0) {
        const { lat, lng } = await getLatLng(results[0]);
        // Horodate la selection : le blur declenche juste avant doit savoir
        // qu'un clic l'a emporte, et renoncer a geocoder le texte partiel.
        derniereSelection.current = Date.now();
        dernierTexteTente.current = null;

        setSelectedFromSuggestions(true);
        setFieldValue(name, address);
        setFieldTouched(name, true);
        setFieldError(name, "");
        poserCoordonnees(lat, lng);
      } 
      if (onAdresseSelected) {
        onAdresseSelected();
      }
    
    } catch (error) {

      console.error("Erreur lors de la sélection d'adresse:", error);
      setFieldError(name, "Vous devez sélectionner une adresse depuis les suggestions !");
    }
  };

  return (
    <Col>
      <PlacesAutocomplete
        value={value}
        onChange={handleAddressChange}
        onSelect={(address, event) => handleAddressSelect(address, event)}
        searchOptions={searchOptions}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="form-floating">
            <input
              {...getInputProps({
                placeholder: `${name}`,
                className:
                  className === "backWhite"
                    ? "form-control backWhite"
                    : "form-control mb-3",
                onBlur: customHandleBlur, // Utilise notre gestionnaire personnalisé          
                  // Empêche toujours la soumission du formulaire

                  // if (event.key === "Enter") {
                  //   setFieldError(name, "Vous devez sélectionner une adresse depuis les suggestions !");
                  //   event.preventDefault();
                    

                  //   // Logique de réinitialisation
                  //   setSelectedFromSuggestions(false);
                  //   //setFieldValue(name, "");
                  //   setFieldTouched(name, true);

                  //   // Réinitialisation conditionnelle selon le champ
                  //   if (name === "pickupAdr") {
                  //     setFieldValue("altitude_Fournisseur", "");
                  //     setFieldValue("longitude_Fournisseur", "");
                  //     setFieldTouched("altitude_Fournisseur", false);
                  //     setFieldTouched("longitude_Fournisseur", false);
                  //     setFieldError(name, "Vous devez sélectionner une adresse depuis les suggestions !");
                  //   }
                  //   else if (name === "address") {
                  //     setFieldValue("altitude_Livreur", "");
                  //     setFieldValue("longitude_Livreur", "");
                  //     setFieldTouched("altitude_Livreur", false);
                  //     setFieldTouched("longitude_Livreur", false);
                  //     setFieldError(name, "Vous devez sélectionner une adresse depuis les suggestions !");
                  //   }

                  //   // Debugging - utilisez les valeurs directement depuis les props
                  //   console.log("Valeurs réinitialisées :", {
                  //     [name]: value,

                  //     altitude: name === "pickupAdr" ? "" : "",
                  //     longitude: name === "address" ? "" : ""
                  //   });
                   
                  // }
                // },
                //onKeyDown: handleAddressChange,  // <-- Ajout pour gérer "Enter"
                onKeyDown: (event) => {
                  // Vérifier si c'est la touche "Enter"
                  if (event.key === "Enter") { 
                    event.preventDefault(); // Empêcher la soumission
                    setFieldError(name, "Vous devez sélectionner une adresse depuis les suggestions !");
                  }
                },
             
              })}
            />
            <label htmlFor="floatingAdr" className="colorInput">
              {name}

            </label>

            <div
              className={`container-autocomplete ${suggestions.length > 0 ? "d-block" : "d-none"
                }`}
            >
              {loading && <div>Chargement...</div>}
              {suggestions.map((suggestion) => {
                const style = {
                  backgroundColor: suggestion.active ? "#41B6E6" : "#fff",
                  cursor: "pointer",
                };
                return (
                  <div
                    className="mapItem"
                    {...getSuggestionItemProps(suggestion, { style })}
                    key={suggestion.placeId}
                  >
                    {suggestion.description}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
    </Col>
  );
};

export default PlacesAutocompleteComponent;